<div class="organization-actions">
  <!-- Header -->
  <div class="section-header">
    <div class="header-content">
      <h2>Organization Actions</h2>
      <p class="section-description">Configure actions available to this organization</p>
    </div>
    <div class="header-controls">
      <button class="btn btn-primary" (click)="openActionGallery()" [disabled]="!selectedOrganization">
        <i class="fa fa-plus"></i> Add from Gallery
      </button>
    </div>
  </div>

  <!-- Messages -->
  <div class="message error-message" *ngIf="errorMessage">
    <i class="fa fa-exclamation-circle"></i> {{ errorMessage }}
    <button class="close-btn" (click)="errorMessage = ''">&times;</button>
  </div>
  <div class="message success-message" *ngIf="successMessage">
    <i class="fa fa-check-circle"></i> {{ successMessage }}
  </div>

  <!-- Loading State -->
  <div class="loading-container" *ngIf="isLoading">
    <i class="fa fa-spinner fa-spin"></i> Loading organization actions...
  </div>

  <!-- Actions Card List -->
  <div *ngIf="!isLoading && selectedOrganization" class="actions-list">
    <!-- Empty State -->
    <div *ngIf="orgActions.length === 0" class="empty-state card">
      <i class="fa fa-bolt"></i>
      <p>No actions configured for this organization</p>
      <button class="btn btn-primary" (click)="openActionGallery()">Browse Action Gallery</button>
    </div>

    <!-- Action Cards -->
    <div *ngFor="let display of orgActions" class="action-card card" [class.editing]="display.isEditing">
      <!-- View Mode -->
      <ng-container *ngIf="!display.isEditing">
        <div class="action-header">
          <div class="action-icon">
            <i [class]="getActionIconClass(display)"></i>
          </div>
          <div class="action-info">
            <h4>{{ getActionName(display) }}</h4>
            <div class="action-meta">
              <span class="status-badge" [ngClass]="getStatusClass(display.orgAction.Status)">
                {{ display.orgAction.Status }}
              </span>
              <span class="scope-badge" [class.scope-global]="getAgentScopeDisplayText(display.orgAction.Configuration) === 'All Agents'" [class.scope-specific]="getAgentScopeDisplayText(display.orgAction.Configuration) !== 'All Agents'">
                {{ getAgentScopeDisplayText(display.orgAction.Configuration) }}
              </span>
              <span *ngIf="hasCredentialsConfigured(display)" class="creds-badge creds-configured" title="Credentials configured">
                <i class="fa fa-key"></i>
              </span>
              <span *ngIf="!hasCredentialsConfigured(display) && display.izzyAction?.RequiresCredentials" class="creds-badge creds-missing" title="Credentials required">
                <i class="fa fa-exclamation-triangle"></i>
              </span>
              <span
                *ngIf="display.channelOverrides.length > 0"
                class="channel-badge"
                (click)="toggleChannelOverrides(display)"
                title="Click to view channel overrides"
              >
                <i class="fa fa-broadcast-tower"></i>
                {{ display.channelOverrides.length }} channel{{ display.channelOverrides.length !== 1 ? 's' : '' }}
              </span>
            </div>
          </div>
        </div>
        <p class="action-description" *ngIf="getActionDescription(display)">{{ getActionDescription(display) }}</p>
        <div class="action-details" *ngIf="hasCredentialsConfigured(display)">
          <div class="detail-item">
            <span class="detail-label">Credential:</span>
            <span class="detail-value">{{ getCredentialName(display.orgAction.CredentialID) }}</span>
          </div>
        </div>

        <!-- Channel Overrides Section -->
        <div class="channel-overrides-section" *ngIf="shouldShowChannelOverrides(display)">
          <!-- Toggle Button -->
          <button
            class="channel-overrides-toggle"
            (click)="toggleChannelOverrides(display)"
            [class.expanded]="display.channelOverridesExpanded"
          >
            <i class="fa fa-broadcast-tower"></i>
            <span *ngIf="display.channelOverrides.length === 0">Configure per-channel overrides</span>
            <span *ngIf="display.channelOverrides.length > 0">
              {{ display.channelOverrides.length }} channel override{{ display.channelOverrides.length !== 1 ? 's' : '' }}
            </span>
            <i class="fa" [class.fa-chevron-down]="!display.channelOverridesExpanded" [class.fa-chevron-up]="display.channelOverridesExpanded"></i>
          </button>

          <!-- Expanded Panel -->
          <div class="channel-overrides-panel" *ngIf="display.channelOverridesExpanded">
            <!-- Existing Overrides List -->
            <div class="channel-override-list" *ngIf="display.channelOverrides.length > 0">
              <div
                class="channel-override-item"
                *ngFor="let override of display.channelOverrides"
                [class.editing]="display.editingChannelId === override.channel.ID"
              >
                <!-- View Mode -->
                <ng-container *ngIf="display.editingChannelId !== override.channel.ID">
                  <div class="channel-override-info">
                    <div class="channel-name">
                      <i class="fa fa-broadcast-tower"></i>
                      {{ override.channel.Name }}
                    </div>
                    <div class="channel-override-meta">
                      <span class="status-badge" [ngClass]="getChannelOverrideStatusClass(override)">
                        {{ override.status }}
                      </span>
                      <span class="scope-badge" [class.scope-global]="override.agentScopeMode === 'global'" [class.scope-specific]="override.agentScopeMode === 'specific'">
                        {{ override.agentScopeMode === 'specific' ? 'Specific Agents' : 'All Agents' }}
                      </span>
                      <span *ngIf="override.credentialId" class="creds-badge creds-configured" title="Custom credentials">
                        <i class="fa fa-key"></i>
                      </span>
                    </div>
                  </div>
                  <div class="channel-override-actions">
                    <button class="btn btn-icon" title="Edit" (click)="startChannelOverrideEdit(display, override.channel.ID)" [disabled]="isSaving">
                      <i class="fa fa-edit"></i>
                    </button>
                    <button class="btn btn-icon btn-danger" title="Remove override" (click)="confirmRemoveChannelOverride(display, override)" [disabled]="isSaving">
                      <i class="fa fa-trash"></i>
                    </button>
                  </div>
                </ng-container>

                <!-- Edit Mode -->
                <ng-container *ngIf="display.editingChannelId === override.channel.ID">
                  <div class="channel-override-edit-form">
                    <div class="edit-form-header">
                      <i class="fa fa-broadcast-tower"></i>
                      <span>{{ override.channel.Name }}</span>
                    </div>

                    <div class="form-row">
                      <div class="form-group">
                        <label>Status</label>
                        <select [(ngModel)]="display.channelOverrideEditForm.status">
                          <option value="Active">Active</option>
                          <option value="Disabled">Disabled</option>
                        </select>
                      </div>
                      <div class="form-group">
                        <label>Credential</label>
                        <select [(ngModel)]="display.channelOverrideEditForm.credentialId">
                          <option [ngValue]="null">-- Inherit from org --</option>
                          <option *ngFor="let cred of credentials" [ngValue]="cred.ID">{{ displayCredentialName(cred.Name) }}</option>
                        </select>
                      </div>
                    </div>

                    <!-- Agent Scope -->
                    <div class="channel-agent-scope">
                      <label>Agent Scope</label>
                      <div class="scope-toggle-mini">
                        <label class="toggle-option-mini" [class.selected]="display.channelOverrideEditForm.agentScopeMode === 'global'">
                          <input type="radio" [(ngModel)]="display.channelOverrideEditForm.agentScopeMode" value="global" [name]="'channelAgentScope_' + override.channel.ID" />
                          <i class="fa fa-globe"></i> All Agents
                        </label>
                        <label class="toggle-option-mini specific" [class.selected]="display.channelOverrideEditForm.agentScopeMode === 'specific'">
                          <input type="radio" [(ngModel)]="display.channelOverrideEditForm.agentScopeMode" value="specific" [name]="'channelAgentScope_' + override.channel.ID" />
                          <i class="fa fa-crosshairs"></i> Specific
                        </label>
                      </div>

                      <div class="agent-chips-mini" *ngIf="display.channelOverrideEditForm.agentScopeMode === 'specific'">
                        <label
                          *ngFor="let agent of availableSubAgents"
                          class="agent-chip-mini"
                          [class.selected]="isChannelOverrideAgentSelected(display, agent.id)"
                        >
                          <input
                            type="checkbox"
                            [checked]="isChannelOverrideAgentSelected(display, agent.id)"
                            (change)="toggleChannelOverrideAgentSelection(display, agent.id)"
                          />
                          <span>{{ agent.displayName }}</span>
                        </label>
                      </div>
                    </div>

                    <div class="form-actions">
                      <button class="btn btn-secondary btn-sm" (click)="cancelChannelOverrideEdit(display)" [disabled]="isSaving">Cancel</button>
                      <button class="btn btn-primary btn-sm" (click)="saveChannelOverride(display)" [disabled]="isSaving">
                        <i class="fa fa-spinner fa-spin" *ngIf="isSaving"></i>
                        {{ isSaving ? 'Saving...' : 'Save' }}
                      </button>
                    </div>
                  </div>
                </ng-container>
              </div>
            </div>

            <!-- Add New Override Section -->
            <div class="add-channel-override" *ngIf="getChannelsWithoutOverride(display).length > 0 && !display.editingChannelId">
              <span class="add-label">Add override for:</span>
              <div class="channel-buttons">
                <button
                  class="add-channel-btn"
                  *ngFor="let channel of getChannelsWithoutOverride(display)"
                  (click)="startChannelOverrideEdit(display, channel.ID)"
                  [disabled]="isSaving"
                >
                  <i class="fa fa-plus"></i> {{ channel.Name }}
                </button>
              </div>
            </div>

            <!-- New Override Edit Form (when adding a new one) -->
            <div class="channel-override-item editing new-override" *ngIf="isEditingNewChannelOverride(display)">
              <div class="channel-override-edit-form">
                <div class="edit-form-header">
                  <i class="fa fa-broadcast-tower"></i>
                  <span>{{ getChannelName(display.editingChannelId) }}</span>
                  <span class="new-badge">New Override</span>
                </div>

                <div class="form-row">
                  <div class="form-group">
                    <label>Status</label>
                    <select [(ngModel)]="display.channelOverrideEditForm.status">
                      <option value="Active">Active</option>
                      <option value="Disabled">Disabled</option>
                    </select>
                  </div>
                  <div class="form-group">
                    <label>Credential</label>
                    <select [(ngModel)]="display.channelOverrideEditForm.credentialId">
                      <option [ngValue]="null">-- Inherit from org --</option>
                      <option *ngFor="let cred of credentials" [ngValue]="cred.ID">{{ displayCredentialName(cred.Name) }}</option>
                    </select>
                  </div>
                </div>

                <!-- Agent Scope -->
                <div class="channel-agent-scope">
                  <label>Agent Scope</label>
                  <div class="scope-toggle-mini">
                    <label class="toggle-option-mini" [class.selected]="display.channelOverrideEditForm.agentScopeMode === 'global'">
                      <input type="radio" [(ngModel)]="display.channelOverrideEditForm.agentScopeMode" value="global" [name]="'newChannelAgentScope_' + display.editingChannelId" />
                      <i class="fa fa-globe"></i> All Agents
                    </label>
                    <label class="toggle-option-mini specific" [class.selected]="display.channelOverrideEditForm.agentScopeMode === 'specific'">
                      <input type="radio" [(ngModel)]="display.channelOverrideEditForm.agentScopeMode" value="specific" [name]="'newChannelAgentScope_' + display.editingChannelId" />
                      <i class="fa fa-crosshairs"></i> Specific
                    </label>
                  </div>

                  <div class="agent-chips-mini" *ngIf="display.channelOverrideEditForm.agentScopeMode === 'specific'">
                    <label
                      *ngFor="let agent of availableSubAgents"
                      class="agent-chip-mini"
                      [class.selected]="isChannelOverrideAgentSelected(display, agent.id)"
                    >
                      <input
                        type="checkbox"
                        [checked]="isChannelOverrideAgentSelected(display, agent.id)"
                        (change)="toggleChannelOverrideAgentSelection(display, agent.id)"
                      />
                      <span>{{ agent.displayName }}</span>
                    </label>
                  </div>
                </div>

                <div class="form-actions">
                  <button class="btn btn-secondary btn-sm" (click)="cancelChannelOverrideEdit(display)" [disabled]="isSaving">Cancel</button>
                  <button class="btn btn-primary btn-sm" (click)="saveChannelOverride(display)" [disabled]="isSaving">
                    <i class="fa fa-spinner fa-spin" *ngIf="isSaving"></i>
                    {{ isSaving ? 'Saving...' : 'Create Override' }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Empty State -->
            <div class="channel-overrides-empty" *ngIf="display.channelOverrides.length === 0 && getChannelsWithoutOverride(display).length === 0">
              <p>No channels available for overrides</p>
            </div>
          </div>
        </div>

        <div class="action-buttons">
          <button class="btn btn-secondary" (click)="startEdit(display)" [disabled]="isSaving">
            <i class="fa fa-edit"></i> Edit
          </button>
          <button class="btn btn-icon btn-danger" title="Remove" (click)="confirmDelete(display)" [disabled]="isSaving">
            <i class="fa fa-trash"></i>
          </button>
        </div>
      </ng-container>

      <!-- Edit Mode -->
      <ng-container *ngIf="display.isEditing">
        <div class="edit-form">
          <div class="edit-header">
            <div class="action-icon">
              <i [class]="getActionIconClass(display)"></i>
            </div>
            <h4>{{ getActionName(display) }}</h4>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Status</label>
              <select [(ngModel)]="display.editForm.status">
                <option value="Active">Active</option>
                <option value="Disabled">Disabled</option>
              </select>
            </div>

            <div class="form-group">
              <label>Credential</label>
              <select [(ngModel)]="display.editForm.credentialId">
                <option [ngValue]="null">-- No credential --</option>
                <option *ngFor="let cred of credentials" [ngValue]="cred.ID">{{ displayCredentialName(cred.Name) }}</option>
              </select>
            </div>
          </div>

          <!-- Agent Scope Section -->
          <div class="form-section agent-scope-section">
            <h4><i class="fa fa-users-cog"></i> Agent Scope</h4>
            <p class="section-description">Control which parts of Izzy can use this action</p>

            <div class="scope-toggle">
              <label class="toggle-option" [class.selected]="display.editForm.agentScopeMode === 'global'">
                <input type="radio" [(ngModel)]="display.editForm.agentScopeMode" value="global" [name]="'editAgentScope_' + display.orgAction.ID" />
                <i class="fa fa-globe"></i>
                <span class="toggle-label">All Agents</span>
                <span class="toggle-description">Available to all parts of Izzy</span>
              </label>
              <label class="toggle-option specific-agents" [class.selected]="display.editForm.agentScopeMode === 'specific'">
                <input type="radio" [(ngModel)]="display.editForm.agentScopeMode" value="specific" [name]="'editAgentScope_' + display.orgAction.ID" />
                <i class="fa fa-crosshairs"></i>
                <span class="toggle-label">Specific Agents</span>
                <span class="toggle-description">Choose which agents can use this action</span>
              </label>
            </div>

            <div class="agent-selection" *ngIf="display.editForm.agentScopeMode === 'specific'">
              <div class="agent-chips">
                <label
                  *ngFor="let agent of availableSubAgents"
                  class="agent-chip"
                  [class.selected]="isAgentSelected(display.editForm, agent.id)"
                >
                  <input
                    type="checkbox"
                    [checked]="isAgentSelected(display.editForm, agent.id)"
                    (change)="toggleAgentSelection(display.editForm, agent.id)"
                  />
                  <i [class]="agent.iconClass"></i>
                  <span>{{ agent.displayName }}</span>
                </label>
              </div>
              <p class="help-text" *ngIf="display.editForm.selectedAgentIds.length === 0">
                <i class="fa fa-info-circle"></i> Select at least one agent
              </p>
            </div>
          </div>

          <div class="form-actions">
            <button class="btn btn-secondary" (click)="cancelEdit(display)" [disabled]="isSaving">Cancel</button>
            <button class="btn btn-primary" (click)="saveEdit(display)" [disabled]="isSaving || (display.editForm.agentScopeMode === 'specific' && display.editForm.selectedAgentIds.length === 0)">
              <i class="fa fa-spinner fa-spin" *ngIf="isSaving"></i>
              {{ isSaving ? 'Saving...' : 'Save' }}
            </button>
          </div>
        </div>
      </ng-container>
    </div>
  </div>

  <!-- Confirm Dialog -->
  <div *ngIf="confirmDialogVisible" class="modal-overlay">
    <div class="modal-dialog">
      <h3>{{ confirmDialogTitle }}</h3>
      <p>{{ confirmDialogMessage }}</p>
      <div class="modal-actions">
        <button class="btn btn-danger" (click)="confirmDialogYes()">Yes, Remove</button>
        <button class="btn btn-secondary" (click)="closeConfirmDialog()">Cancel</button>
      </div>
    </div>
  </div>

  <!-- Gallery Modal -->
  <div *ngIf="galleryModalVisible" class="modal-overlay" (click)="closeGalleryModal()">
    <div class="gallery-modal" (click)="$event.stopPropagation()">
      <div class="gallery-modal-header">
        <h3>Add Actions</h3>
        <button class="close-btn" (click)="closeGalleryModal()">&times;</button>
      </div>

      <div class="gallery-modal-toolbar">
        <div class="gallery-search">
          <i class="fa fa-search"></i>
          <input
            type="text"
            placeholder="Search actions..."
            [(ngModel)]="gallerySearchTerm"
          />
        </div>
      </div>

      <div class="gallery-modal-body">
        <!-- Category Sidebar -->
        <div class="gallery-categories">
          <div
            class="gallery-category-item"
            [class.selected]="!gallerySelectedCategoryId"
            (click)="selectGalleryCategory(null)"
          >
            <i class="fa fa-th-large"></i>
            <span>All Actions</span>
          </div>

          <ng-container *ngFor="let category of rootCategories">
            <div
              class="gallery-category-item"
              [class.selected]="gallerySelectedCategoryId === category.ID"
              (click)="selectGalleryCategory(category.ID)"
            >
              <i [class]="category.IconClass || 'fa fa-folder'"></i>
              <span>{{ category.Name }}</span>
            </div>

            <!-- Child categories -->
            <ng-container *ngFor="let child of getCategoryChildren(category.ID)">
              <div
                class="gallery-category-item child"
                [class.selected]="gallerySelectedCategoryId === child.ID"
                (click)="selectGalleryCategory(child.ID)"
              >
                <i [class]="child.IconClass || 'fa fa-folder'"></i>
                <span>{{ child.Name }}</span>
              </div>
            </ng-container>
          </ng-container>
        </div>

        <!-- Action Cards -->
        <div class="gallery-actions">
          <div *ngIf="filteredGalleryActions.length === 0" class="gallery-empty">
            <p>No actions available to add.</p>
          </div>

          <div *ngFor="let card of filteredGalleryActions" class="gallery-action-card">
            <div class="gallery-action-icon">
              <i [class]="card.izzyAction.IconClass || 'fa fa-cog'"></i>
            </div>
            <div class="gallery-action-info">
              <div class="gallery-action-name">{{ card.izzyAction.Name }}</div>
              <div class="gallery-action-description">{{ card.izzyAction.Description }}</div>
              <div class="gallery-action-badges">
                <span class="badge badge-category" *ngIf="card.category">{{ card.category.Name }}</span>
                <span class="badge badge-plan">{{ card.planName }}</span>
                <span class="badge badge-creds" *ngIf="card.izzyAction.RequiresCredentials">
                  <i class="fa fa-key"></i> Requires credentials
                </span>
              </div>
            </div>
            <div class="gallery-action-buttons">
              <button
                class="btn btn-primary btn-sm"
                (click)="addActionFromGallery(card)"
                [disabled]="isAddingAction"
              >
                <i class="fa fa-plus"></i> Add
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="gallery-modal-footer">
        <button class="btn btn-secondary" (click)="closeGalleryModal()">Done</button>
      </div>
    </div>
  </div>
</div>
